import { atom, createStore } from 'jotai';
import { atomWithReset, atomWithStorage, createJSONStorage } from 'jotai/utils';
import { PreloadAttachmentsResponse, SessionData } from '@/modules/api/types';
import { getCookie } from '@/modules/common';
import { BridgeType, ChatFeedbackType, ErrorType, MessageType, PageType, ShopifyCart } from '@/modules/models';
import { PopupState } from '@/modules/models/PopupState';
import { WidgetPopupType } from '@/modules/models/WidgetPopupType';
import {
  CHAT_ID_STORAGE,
  EMBEDDED_OPEN_CHAT_STORAGE,
  OPEN_CHAT_STORAGE,
  SEMI_OPEN_SEEN_STORAGE,
  SEMI_OPEN_STATE_STORAGE,
  SESSION_FINISH_TIME_STORAGE,
  TOKEN_STORAGE,
  VIEWED_PRODUCTS_STORAGE,
  WIDGET_POPUPS_STATE_STORAGE,
} from './constants';

export const atomWithLocalStorage = <T>(key: string, defaultValue: T) =>
  atomWithStorage(key, defaultValue, undefined, { getOnInit: true });

export const atomWithSessionStorage = <T>(key: string, initialValue: T) =>
  atomWithStorage<T>(
    key,
    initialValue,
    createJSONStorage(() => sessionStorage),
    { getOnInit: true }
  );

export const store = createStore();

export const isFontsLoadedAtom = atom(false);

export const isUserAnsweredAtom = atom(false);
export const isMlAnsweredAtom = atom(false);
export const isUnauthorizedAtom = atom(false);
export const requestsQueueAtom = atom<
  {
    url: string;
    method: string;
    headers: HeadersInit;
    body?: string;
  }[]
>([]);

export const messageTextAtom = atom('');
export const attachmentsAtom = atom<PreloadAttachmentsResponse>([]);
export const isLoadingMessageAtom = atom(false);
export const messagesAtom = atom<MessageType[]>([]);

export const DEFAULT_BRIDGE_VALUE = {
  translations: {},
  closeChatWithEsc: true,
  isAlwaysOpenWSConnection: false,
};
export const bridgeAtom = atomWithReset<BridgeType>(DEFAULT_BRIDGE_VALUE as BridgeType);

export const cartAtom = atom<{ isOpen: boolean; products: ShopifyCart[] }>({
  isOpen: false,
  products: [],
});

export const messageTimeoutAtom = atom<number>(-1);
export const chatFeedbackAtom = atom<ChatFeedbackType | null>(null);

// Api atoms

export const sessionAtom = atom<SessionData>({
  uid: '',
  token: '',
  countryCode: '',
  isTestUser: false,
  isInfluencer: false,
  userAgent: '',
  customerId: null,
  localization: '',
  location: null,
});
export const isSessionSentFirstTimeAtom = atom(false);
export const isInitSentAtom = atom(false);

// Local storage atoms

export const tokenAtom = (() => {
  const localToken = localStorage.getItem(TOKEN_STORAGE);
  let token = localToken ? JSON.parse(localToken) : '';

  const cookieToken = getCookie(TOKEN_STORAGE);

  if (cookieToken && token !== cookieToken) {
    token = cookieToken;
  }

  return atomWithLocalStorage(TOKEN_STORAGE, token);
})();

export const chatIdAtom = atomWithLocalStorage<number | null>(CHAT_ID_STORAGE, null);
export const sessionFinishTimeAtom = atomWithLocalStorage(SESSION_FINISH_TIME_STORAGE, 0);
export const mlVersionAtom = atom<string | null>(null);

export const errorAtom = atom<ErrorType | null>(null);

// Session storage atoms
export const isOpenChatAtom = atomWithStorage(
  OPEN_CHAT_STORAGE,
  false,
  createJSONStorage<boolean>(() => sessionStorage)
);

export const isEmbeddedOpenChatAtom = atomWithStorage(
  EMBEDDED_OPEN_CHAT_STORAGE,
  false,
  createJSONStorage<boolean>(() => sessionStorage)
);

export const isResetChatAtom = atom<boolean>(false);
export const isReopenedChatAtom = atom<boolean>(false);
export const isReadyForScrollAtom = atom(false);

// Latches true once DataLayer has mounted and its REOPEN_CHAT/TOGGLE_CHAT listeners are attached.
export const isWidgetReadyAtom = atom<boolean>(false);

export const isLiveAgentModeAtom = atom(false);
export const isLiveAgentAvailableAtom = atom<boolean | null>(null);
export const transferToLiveAgentAtom = atom<(() => void) | null>(null);
export const transferToBotAtom = atom<((email?: string) => void) | null>(null);

export const viewedProductsAtom = atomWithSessionStorage<string[]>(VIEWED_PRODUCTS_STORAGE, []);

export const semiOpenSeenPagesAtom = atomWithSessionStorage<string[]>(SEMI_OPEN_SEEN_STORAGE, []);
export const isSemiOpenChatAtom = atomWithSessionStorage<boolean>(SEMI_OPEN_STATE_STORAGE, false);

export type ChatWindowHeights = {
  firstMessage: number;
  messageList: number;
  header: number;
  footer: number;
  maintenance: number;
};
export const chatWindowHeightsAtom = atom<ChatWindowHeights>({
  firstMessage: 30,
  messageList: 0, // margins/paddings
  header: 0,
  footer: 0,
  maintenance: 0,
});

export const renderedColorsAtom = atom<Record<string, boolean>>({});
export const renderedMessagesAtom = atom<Record<string, boolean>>({});
export const messagesReceivedTimeAtom = atom<Record<string, number>>({});

export type InitState = Omit<
  Record<PageType, PopupState>,
  PageType.CART | PageType.AHAVA_WORDPRESS | PageType.ADD_TO_CART_SUGGESTION | PageType.EMBEDDED
>;
export const widgetPopupsStatesAtom = atomWithSessionStorage<{
  [WidgetPopupType.INIT]: InitState;
  [WidgetPopupType.INIT_WITH_CLOSE_BTN]: PopupState;
  [WidgetPopupType.VIEWED_PRODUCTS]: PopupState;
  [WidgetPopupType.CROSS_SALE]: PopupState;
  [WidgetPopupType.RETURN_VISITOR]: PopupState;
  [WidgetPopupType.STUCK_ON_PAGE]: PopupState;
  [WidgetPopupType.HOVER]: PopupState;
}>(WIDGET_POPUPS_STATE_STORAGE, {
  [WidgetPopupType.INIT]: {
    [PageType.HOME]: PopupState.IDLE,
    [PageType.ABOUT]: PopupState.IDLE,
    [PageType.PRODUCT]: PopupState.IDLE,
    [PageType.DEFAULT]: PopupState.IDLE,
    [PageType.COLLECTION]: PopupState.IDLE,
  },
  [WidgetPopupType.INIT_WITH_CLOSE_BTN]: PopupState.IDLE,
  [WidgetPopupType.VIEWED_PRODUCTS]: PopupState.IDLE,
  [WidgetPopupType.CROSS_SALE]: PopupState.IDLE,
  [WidgetPopupType.RETURN_VISITOR]: PopupState.IDLE,
  [WidgetPopupType.STUCK_ON_PAGE]: PopupState.IDLE,
  [WidgetPopupType.HOVER]: PopupState.IDLE,
});
